import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Check, Search } from "lucide-react";

export default function BuyerMeta() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState("");
  const [nu, setNu] = useState({ buyer: "", city: "", handler: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/buyer-meta")
      .then((r) => { setList(r.data.list || []); setEdits({}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const getVal = (row, field) => {
    const e = edits[row.buyer];
    return e && e[field] !== undefined ? e[field] : row[field];
  };
  const setVal = (row, field, v) => setEdits({ ...edits, [row.buyer]: { ...(edits[row.buyer] || {}), [field]: v } });
  const isDirty = (row) => {
    const e = edits[row.buyer];
    if (!e) return false;
    return (e.city !== undefined && e.city !== row.city) || (e.handler !== undefined && e.handler !== row.handler);
  };

  const saveRow = async (row) => {
    setBusy(true);
    try {
      await api.post("/buyer-meta", { buyer: row.display, city: getVal(row, "city"), handler: getVal(row, "handler") });
      toast.success(`${row.display} updated.`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  const addRow = async (e) => {
    e.preventDefault();
    if (!nu.buyer.trim()) { toast.error("Enter a buyer name."); return; }
    setBusy(true);
    try {
      await api.post("/buyer-meta", { buyer: nu.buyer.trim(), city: nu.city.trim(), handler: nu.handler.trim() });
      toast.success(`${nu.buyer.trim()} saved.`);
      setNu({ buyer: "", city: "", handler: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove ${row.display}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/buyer-meta/${encodeURIComponent(row.buyer)}`);
      toast.success(`${row.display} removed.`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  const filtered = list.filter((r) => [r.display, r.city, r.handler].join(" ").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="buyer-meta-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">Settings</p>
        <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1 flex items-center gap-2">
          <MapPin className="w-7 h-7 text-[#D95D39]" /> Buyers — City & Handler
        </h1>
        <p className="text-sm text-[#8CA3A7] mt-1">Set each buyer's city and the person handling them. Reports can then be segregated by city or handler. {canEdit ? "" : "View only."}</p>
      </div>

      {canEdit && (
        <form onSubmit={addRow} data-testid="add-buyer-form" className="bg-white border border-[#E6E4DD] rounded-sm p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Buyer name</label>
            <input data-testid="new-buyer-name" value={nu.buyer} onChange={(e) => setNu({ ...nu, buyer: e.target.value })} placeholder="e.g. ARVIND EXIM" className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="w-48">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">City</label>
            <input data-testid="new-buyer-city" value={nu.city} onChange={(e) => setNu({ ...nu, city: e.target.value })} placeholder="JAIPUR" className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="w-40">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Handled by</label>
            <input data-testid="new-buyer-handler" value={nu.handler} onChange={(e) => setNu({ ...nu, handler: e.target.value })} placeholder="TANRAJ" className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <button data-testid="add-buyer-btn" disabled={busy} className="bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:bg-[#2A3447] transition-colors disabled:opacity-60">
            <Plus className="w-4 h-4" /> Add / Update
          </button>
        </form>
      )}

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-[#8CA3A7] absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="buyer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buyer, city, handler…" className="w-full rounded-sm border border-[#E6E4DD] pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
      </div>

      <div className="border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
              <th className="px-4 py-3">Buyer</th>
              <th className="px-4 py-3 w-56">City</th>
              <th className="px-4 py-3 w-44">Handled By</th>
              {canEdit && <th className="px-4 py-3 w-24"></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canEdit ? 4 : 3} className="px-4 py-8 text-center text-[#8CA3A7] font-mono text-sm">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={canEdit ? 4 : 3} className="px-4 py-8 text-center text-[#8CA3A7] font-mono text-sm">No buyers found.</td></tr>
            ) : filtered.map((row) => (
              <tr key={row.buyer} data-testid={`buyer-row-${row.buyer}`} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-3 text-[#0A0D14] font-medium">{row.display}</td>
                <td className="px-4 py-3">
                  {canEdit ? (
                    <input data-testid={`city-input-${row.buyer}`} value={getVal(row, "city")} onChange={(e) => setVal(row, "city", e.target.value)} className="w-full rounded-sm border border-[#E6E4DD] px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
                  ) : (row.city || "—")}
                </td>
                <td className="px-4 py-3">
                  {canEdit ? (
                    <input data-testid={`handler-input-${row.buyer}`} value={getVal(row, "handler")} onChange={(e) => setVal(row, "handler", e.target.value)} className="w-full rounded-sm border border-[#E6E4DD] px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
                  ) : (row.handler || "—")}
                </td>
                {canEdit && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`save-buyer-${row.buyer}`} onClick={() => saveRow(row)} disabled={busy || !isDirty(row)} className={`p-1.5 rounded-sm transition-colors ${isDirty(row) ? "text-[#065F46] hover:bg-emerald-50" : "text-[#CBD5E1] cursor-default"}`} title="Save">
                        <Check className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                      <button data-testid={`delete-buyer-${row.buyer}`} onClick={() => removeRow(row)} disabled={busy} className="text-[#991B1B] hover:bg-red-50 p-1.5 rounded-sm transition-colors" title="Remove">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
